import { useEffect, useState } from "react";
import { Alert, App, Button, Card, DatePicker, Form, Input, InputNumber, Statistic, Table, Tag, Tooltip } from "antd";
import type { Dayjs } from "dayjs";
import { listReconciliations, saveReconciliation, systemQty, type ReconciliationRow } from "../api/client";
import { formatWhen, LoadFail, PageSpin } from "../components/widgets";

export function Reconciliation(): JSX.Element {
  const { message } = App.useApp();
  const [rows, setRows] = useState<ReconciliationRow[]>([]);
  const [system, setSystem] = useState(0);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [physical, setPhysical] = useState<number | null>(null);
  const [odoo, setOdoo] = useState<number | null>(null);
  const [week, setWeek] = useState<Dayjs | null>(null);
  const [reason, setReason] = useState("");
  const [resolution, setResolution] = useState("");
  const [resolved, setResolved] = useState(false);

  function load(): void {
    setLoading(true);
    void Promise.all([listReconciliations(), systemQty()])
      .then(([history, qty]) => {
        setRows(history);
        setSystem(qty.systemQty);
        setFailed(false);
      })
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    load();
  }, []);

  if (loading && rows.length === 0) return <PageSpin tip="正在读取对账记录…" />;
  if (failed) return <LoadFail title="这次没有记下。" onRetry={load} />;

  const gapPhysical = physical === null ? null : system - physical;
  const gapOdoo = odoo === null ? null : system - odoo;
  const needsReason = (gapPhysical !== null && gapPhysical !== 0) || (gapOdoo !== null && gapOdoo !== 0);

  return (
    <>
      <Card title="本周对账">
        <Form layout="vertical">
          <Form.Item label="周次">
            <DatePicker picker="week" value={week} onChange={setWeek} />
          </Form.Item>
          <Statistic title={<Tooltip title="含待上架，并加上期初双数。已经入库、还没出库的鞋。"><span>系统已入库双数</span></Tooltip>} value={system} suffix="双" />
          <Form.Item label="专区实物双数" extra="用盘点差异清单汇总，不要口算后不留清单。">
            <InputNumber min={0} value={physical} onChange={(value) => setPhysical(typeof value === "number" ? value : null)} />
          </Form.Item>
          <Form.Item label="Odoo 数量" extra="填 Odoo 里这一专区的数量。">
            <InputNumber min={0} value={odoo} onChange={(value) => setOdoo(typeof value === "number" ? value : null)} />
          </Form.Item>
          <p>系统 − 实物：{gapPhysical ?? "—"}</p>
          <p>系统 − Odoo：{gapOdoo ?? "—"}</p>
          {needsReason ? <Alert type="warning" showIcon message="还有相差。请写清原因，以及今天有没有处理完。不要空着保存。" /> : null}
          <Form.Item label="原因">
            <Input.TextArea value={reason} onChange={(event) => setReason(event.target.value)} />
          </Form.Item>
          <Form.Item label="怎么处理完的">
            <Input.TextArea value={resolution} onChange={(event) => setResolution(event.target.value)} />
          </Form.Item>
          <Button
            onClick={() => setResolved((current) => !current)}
          >
            {resolved ? "标记为已处理完" : "标记为还没处理完"}
          </Button>
          <Button
            type="primary"
            style={{ marginLeft: 8 }}
            disabled={physical === null || odoo === null || !week || (needsReason && !reason.trim())}
            onClick={() => {
              if (physical === null || odoo === null || !week) {
                message.warning("实物双数或 Odoo 数量还空着。两个都填上才能记下。");
                return;
              }
              void saveReconciliation({
                weekKey: week.startOf("week").format("YYYY-MM-DD"),
                physicalQty: physical,
                odooQty: odoo,
                reason,
                resolved,
                resolution,
              })
                .then(() => {
                  message.success("本周对账已记下。相差和处理情况会留在表里。");
                  load();
                })
                .catch((error: unknown) => message.error(error instanceof Error ? error.message : "这次没有记下。"));
            }}
          >
            记下本周对账
          </Button>
        </Form>
      </Card>
      <Table
        style={{ marginTop: 16 }}
        rowKey="id"
        dataSource={rows}
        locale={{ emptyText: "还没有对账记录。填完上面三方数字后，点记下本周对账。" }}
        columns={[
          { title: "周次", dataIndex: "weekKey" },
          { title: "系统", dataIndex: "systemQty" },
          { title: "实物", dataIndex: "physicalQty" },
          { title: "Odoo", dataIndex: "odooQty" },
          { title: "相差", render: (_v: unknown, row: ReconciliationRow) => `实物 ${row.gapPhysical} / Odoo ${row.gapOdoo}` },
          { title: "原因", dataIndex: "reason" },
          { title: "是否处理完", dataIndex: "resolved", render: (done: boolean) => <Tag color={done ? "success" : "error"}>{done ? "已处理完" : "还没处理完"}</Tag> },
          { title: "记录人", dataIndex: "operatorName" },
          { title: "时间", dataIndex: "createdAt", render: (value: string) => formatWhen(value) },
        ]}
      />
    </>
  );
}
